import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { 
  Container, 
  ModalOverlay, 
  ModalContainer, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  DownloadCard, 
  DownloadIconWrapper, 
  ActionButton,
  EmpresaModalContainer
} from "./styles";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import { useTheme } from "styled-components";
import {
  GoFile,
  GoDatabase,
  GoOrganization,
  GoCalendar,
  GoKey,
  GoTrash,
  GoUpload,
  GoArchive,
  GoFileDirectory,
  GoCheck,
  GoAlert,
  GoX,
  GoPackage,
  GoDownload,
  GoChevronRight,
  GoSync,
} from "react-icons/go";
import ContentLoader from "react-content-loader";
import { BiSpreadsheet } from "react-icons/bi";
import { HiOutlineChartBar, HiOutlineDocumentText } from "react-icons/hi";
import { FiFile, FiFolder, FiKey, FiAlertTriangle } from "react-icons/fi";
import Upload from "../upload";






const KpiSkeleton = ({ theme }) => {
  const isDark = theme.body !== "#fafbfc";
  const backgroundColor = isDark ? "#1a1a1a" : "#f0f0f0";
  const foregroundColor = isDark ? "#2a2a2a" : "#e5e7eb";


  return (
    <div style={{ 
      background: theme.card, 
      border: `1px solid ${theme.border || 'transparent'}`,
      padding: '14px', 
      borderRadius: '8px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      <ContentLoader 
        speed={2}
        width="100%"
        height={58}
        backgroundColor={backgroundColor}
        foregroundColor={foregroundColor}
        style={{ width: '100%' }}
      >
        {/* Thumb/Icon */}
        <circle cx="12" cy="18" r="10" />
        
        {/* Value */}
        <rect x="36" y="8" rx="3" ry="3" width="80" height="14" />
        
        {/* Label */}
        <rect x="36" y="28" rx="3" ry="3" width="60" height="10" />
      </ContentLoader>
    </div>
  );
};



const CompanyCardSkeleton = ({ theme }) => {
  const isDark = theme.body !== "#fafbfc";
  const backgroundColor = isDark ? "#1a1a1a" : "#f0f0f0";
  const foregroundColor = isDark ? "#2a2a2a" : "#e5e7eb";

  return (
    <div style={{ 
      background: theme.card, 
      padding: '20px', 
      borderRadius: '12px',
      marginBottom: '20px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.02)'
    }}>
      <ContentLoader 
        speed={2}
        width="100%"
        height={540}
        backgroundColor={backgroundColor}
        foregroundColor={foregroundColor}
        style={{ width: '100%' }}
      >
      {/* Title */}
      <rect x="0" y="0" rx="4" ry="4" width="200" height="24" />
      
      {/* Periodos Box */}
      <rect x="0" y="40" rx="8" ry="8" width="48%" height="50" />
      <rect x="52%" y="40" rx="8" ry="8" width="48%" height="50" />
      
      {/* XMLs */}
      <rect x="0" y="110" rx="3" ry="3" width="100" height="15" />
      <rect x="85%" y="110" rx="3" ry="3" width="15%" height="15" />
      <rect x="0" y="135" rx="3" ry="3" width="100%" height="6" />
      
      {/* NFe */}
      <rect x="0" y="160" rx="3" ry="3" width="100" height="15" />
      <rect x="85%" y="160" rx="3" ry="3" width="15%" height="15" />
      <rect x="0" y="185" rx="3" ry="3" width="100%" height="6" />

      {/* CTe */}
      <rect x="0" y="210" rx="3" ry="3" width="100" height="15" />
      <rect x="85%" y="210" rx="3" ry="3" width="15%" height="15" />
      <rect x="0" y="235" rx="3" ry="3" width="100%" height="6" />

      {/* SPED ICMS */}
      <rect x="0" y="260" rx="3" ry="3" width="100" height="15" />
      <rect x="85%" y="260" rx="3" ry="3" width="15%" height="15" />
      <rect x="0" y="285" rx="3" ry="3" width="100%" height="6" />

      {/* SPED PIS */}
      <rect x="0" y="310" rx="3" ry="3" width="100" height="15" />
      <rect x="85%" y="310" rx="3" ry="3" width="15%" height="15" />
      <rect x="0" y="335" rx="3" ry="3" width="100%" height="6" />

      {/* Certificate */}
      <rect x="0" y="380" rx="4" ry="4" width="100%" height="40" />

      <rect x="0" y="440" rx="4" ry="4" width="100%" height="80" />
    </ContentLoader>
  </div>
  );
};


const formatNumber = (num) => {
  return (num || 0).toLocaleString("pt-BR");
};

export default function Files() {
  const { currentProject, user } = useAuth();
  const theme = useTheme();
  const [activeTab, setActiveTab] = usePersistedState(
    "vidal_files_tab",
    "empresas"
  );
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [showUploadScreen, setShowUploadScreen] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Verificar permissões
  // god, admin e users têm acesso ao dashboard completo
  const hasDashboardAccess = ['god', 'admin', 'users', 'user'].includes(user?.cargo);
  // Apenas god pode resetar 
  const isGod = user?.cargo === 'god';
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetProgress, setResetProgress] = useState(0);
  const [resetStage, setResetStage] = useState('');
  const [resetOperationId, setResetOperationId] = useState(null);
  const [activeSection, setActiveSection] = useState('informacoes'); // 'informacoes' ou 'arquivos'
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [spedDownloadProgress, setSpedDownloadProgress] = useState(0);
  const [isDownloadingSped, setIsDownloadingSped] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  // Modal de Detalhes da Empresa (substitui drilldown)
  const [empresaModal, setEmpresaModal] = useState(null);


  // Buscar dados do projeto
  const fetchProjectData = useCallback(async () => {
    const projectId = localStorage.getItem('vidal_selected_project') || currentProject?._id || currentProject?.id;
    if (!projectId) {
      console.log("[DEBUG] projectId não encontrado");
      return;
    }

    console.log(`[DEBUG] Buscando dados do files-dashboard para projeto ${projectId}`);

    try {
      setLoading(true);
      
      // Chama diretamente a rota específica do dashboard
      const { data } = await api.get(`/projects/${projectId}/files-dashboard`);
      console.log("[DEBUG] Dashboard carregado:", data);
      
      setProjectData(data);
    } catch (error) {
      console.error("Erro ao buscar dados do projeto:", error);
    } finally {
      setLoading(false);
    }
  }, [currentProject]);


  const lastFetchedProjectId = useRef(null);

  useEffect(() => {
    const projectId = localStorage.getItem('vidal_selected_project') || currentProject?._id || currentProject?.id;
    
    // Se não tiver projeto ou form o mesmo que ja buscamos, ignora
    if (!projectId || lastFetchedProjectId.current === projectId) {
      return; 
    }

    console.log("[DEBUG] fetchProjectData chamada (novo projeto)", projectId);
    lastFetchedProjectId.current = projectId;
    fetchProjectData();
  }, [fetchProjectData, currentProject]);

  // Função para reprocessar SPEDs já processados
  const handleReprocessSpeds = async () => {
    const projectId = localStorage.getItem('vidal_selected_project') || currentProject?._id;
    if (!projectId) {
      alert('Nenhum projeto selecionado.');
      return;
    }

    setReprocessing(true);
    try {
      // Primeiro, tentar reprocessar diretamente
      const { data } = await api.post(`/speds/reprocess/${projectId}`);
      console.log('[Files] Reprocessamento iniciado:', data);
      
      // Determinar tipo de SPED para a tela de processamento
      const hasIcms = data.files?.some(f => f.categoria === 'icms');
      const hasPis = data.files?.some(f => f.categoria === 'pis');
      const hasMixed = data.files?.some(f => f.categoria === 'mixed');
      
      let spedType = 'icms'; // default
      if (hasMixed || (hasIcms && hasPis)) {
        spedType = 'mixed';
      } else if (hasPis) {
        spedType = 'pis';
      } else if (hasIcms) {
        spedType = 'icms';
      }
      
      console.log('[Files] Detected SPED types:', { hasIcms, hasPis, hasMixed, spedType });
      
      // Salvar estado no localStorage para persistência
      localStorage.setItem(`ravena_active_upload_${projectId}`, JSON.stringify({
        stage: 'processing',
        operationId: null,
        spedType: spedType,
        timestamp: Date.now()
      }));
      
      // Redirecionar para a tela de upload (que mostrará o ProcessingScreen)
      setReprocessing(false);
      setShowUploadScreen(true);
      
    } catch (error) {
      console.error('[Files] Erro ao reprocessar:', error);
      const errorData = error.response?.data;
      const errorMsg = errorData?.error || error.message || 'Erro desconhecido';
      
      // Se o erro indica que há arquivos em 'failed', oferecer para movê-los
      if (errorData?.details?.failed > 0 && errorMsg.includes('failed')) {
        const failedCount = errorData.details.failed;
        if (window.confirm(`Encontrados ${failedCount} arquivo(s) na pasta 'failed'. Deseja movê-los para 'processed' e tentar reprocessar?`)) {
          try {
            // Mover arquivos de failed para processed
            const moveResult = await api.post(`/speds/move-failed-to-processed/${projectId}`);
            console.log('[Files] Arquivos movidos:', moveResult.data);
            
            // Tentar reprocessar novamente
            alert(`${moveResult.data.movedCount} arquivo(s) movido(s). Iniciando reprocessamento...`);
            handleReprocessSpeds(); // Recursão para tentar novamente
            return;
          } catch (moveError) {
            console.error('[Files] Erro ao mover arquivos:', moveError);
            alert(`Erro ao mover arquivos: ${moveError.response?.data?.error || moveError.message}`);
          }
        }
      } else {
        alert(`Erro ao iniciar reprocessamento: ${errorMsg}`);
      }
      
      setReprocessing(false);
    }
  };

  // Função para resetar dados SPED do projeto (agora assíncrona com progresso)
  const handleResetSpedData = async () => {
    const projectId = localStorage.getItem('vidal_selected_project');
    if (!projectId) return;

    setResetting(true);
    setResetProgress(0);
    setResetStage('Iniciando...');

    try {
      // 1. Chamar API que enfileira a task
      const { data } = await api.delete(`/projects/${projectId}/reset-sped-data`);
      console.log("[RESET] Task enfileirada:", data);
      setResetOperationId(data.operationId);
      setResetStage('Conectando ao servidor de logs...');

      // 2. Conectar ao WebSocket para receber progresso
      const WS_URL = process.env.REACT_APP_WS_URL || 'wss://log.vidal-app.com/ws';
      const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
      const token = authData.token;
      
      const ws = new WebSocket(`${WS_URL}?token=${token}`);
      
      ws.onopen = () => {
        console.log('[RESET] WebSocket conectado');
        ws.send(JSON.stringify({
          action: 'subscribe',
          type: 'reset_sped_data',
          projectId: projectId
        }));
      };

      ws.onmessage = (event) => {
        try {
          const rawMsg = JSON.parse(event.data);
          
          // Ignorar mensagens de sistema
          if (rawMsg.success && rawMsg.message === 'Inscrição realizada com sucesso') return;
          if (rawMsg.message?.type === 'info') return;
          
          // O servidor WebSocket encapsula as mensagens em {success: true, message: data}
          const msg = rawMsg.message || rawMsg;
          
          console.log('[RESET] Received message:', msg);
          
          if (msg.type === 'reset_sped_data' && msg.projectId === projectId) {
            const { progress, stage, etapa, totalDeleted } = msg.data || {};
            
            if (progress !== undefined) {
              setResetProgress(progress);
            }
            if (stage) {
              setResetStage(stage);
            }
            
            // Se completou
            if (etapa === 'completed' || progress >= 100) {
              setResetStage(`Concluído! ${totalDeleted?.toLocaleString() || 0} documentos deletados.`);
              setTimeout(() => {
                setShowResetModal(false);
                setResetting(false);
                setResetProgress(0);
                setResetStage('');
                setResetOperationId(null);
                ws.close();
                // Recarregar dados
                fetchProjectData();
              }, 2000);
            }
          }
        } catch (e) {
          console.error('[RESET] Erro ao processar mensagem WS:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('[RESET] WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('[RESET] WebSocket fechado');
      };

    } catch (error) {
      console.error("Erro ao resetar dados:", error);
      alert("Erro ao iniciar reset: " + (error.response?.data?.error || error.message));
      setResetting(false);
      setResetProgress(0);
      setResetStage('');
    }
  };

  // No modo demo, abrir tela de upload automaticamente quando o tutorial chegar na Tela de Arquivos
  useEffect(() => {
    const handler = () => setShowUploadScreen(true);
    window.addEventListener('demo-open-files-upload', handler);
    return () => window.removeEventListener('demo-open-files-upload', handler);
  }, []);

  // Buscar arquivos enviados
  const handleDownloadMissingKeys = async (companyId = null, period = null) => {
    const projectId = localStorage.getItem('vidal_selected_project') || currentProject?._id || currentProject?.id;
    if (!projectId) {
      alert("Nenhum projeto selecionado.");
      return;
    }
    
    try {
      const params = {};
      if (companyId) params.companyId = companyId;
      if (period) params.period = period;

      const response = await api.get(`/projects/${projectId}/download/missing-keys`, {
        params,
        responseType: 'blob'
      });
      
      const filenameParts = ['chaves_faltantes', projectId];
      if (companyId) filenameParts.push(companyId.replace(/\D/g, ''));
      if (period) filenameParts.push(period);
      
      const fileName = `${filenameParts.join('_')}.txt`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      alert("Erro ao baixar chaves faltantes. Tente novamente mais tarde.");
    }
  };

  const handleDownloadSpedFiles = async () => {
    const projectId = localStorage.getItem('vidal_selected_project') || currentProject?._id || currentProject?.id;
    if (!projectId) {
      alert("Nenhum projeto selecionado.");
      return;
    }
    
    setIsDownloadingSped(true);
    setSpedDownloadProgress(0);
    
    try {
      const response = await api.get(`/projects/${projectId}/download/sped-files`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setSpedDownloadProgress(percentCompleted);
          } else {
            // Se o servidor não enviar Content-Length, simular progresso com bytes recebidos
            const mbReceived = (progressEvent.loaded / (1024 * 1024)).toFixed(1);
            setSpedDownloadProgress(prev => Math.min(prev + 5, 95)); // Incrementar até 95%
          }
        }
      });
      
      setSpedDownloadProgress(100);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `speds_${projectId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Reset após 1 segundo
      setTimeout(() => {
        setSpedDownloadProgress(0);
        setIsDownloadingSped(false);
      }, 1000);
      
    } catch (err) {
      console.error(err);
      setSpedDownloadProgress(0);
      setIsDownloadingSped(false);
      if (err.response?.status === 404) {
        alert("Nenhum arquivo SPED encontrado para este projeto.");
      } else {
        alert("Erro ao baixar arquivos SPED. Tente novamente mais tarde.");
      }
    }
  };

  const handleDownloadFilesReport = async () => {
    const projectId = localStorage.getItem('vidal_selected_project') || currentProject?._id || currentProject?.id;
    if (!projectId) {
      alert("Nenhum projeto selecionado.");
      return;
    }
    
    try {
      const response = await api.get(`/projects/${projectId}/download/files-report`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_arquivos_${projectId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        alert("Nenhuma empresa encontrada para gerar relatório.");
      } else {
        alert("Erro ao gerar relatório. Tente novamente mais tarde.");
      }
    }
  };

  const fetchUploadedFiles = useCallback(async () => {
    const projectId = localStorage.getItem('vidal_selected_project');
    if (!projectId) return;

    setLoadingFiles(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/uploads`);
      setUploadedFiles(data.uploads || []);
    } catch (error) {
      console.error("Erro ao buscar arquivos:", error);
      setUploadedFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  // Buscar arquivos quando mudar para aba de arquivos
  useEffect(() => {
    if (activeSection === 'arquivos') {
      fetchUploadedFiles();
    }
  }, [activeSection, fetchUploadedFiles]);

  // Transformar dados do projeto para o formato esperado pela UI
  const dashboardData = useMemo(() => {
    if (!projectData) return null;

    // Defaults para evitar crash
    const defaults = { total: 0, processados: 0 };
    
    return {
      empresasConectadas: projectData.empresasConectadas || { total: 0, processados: 0 },
      speds: projectData.speds || defaults,
      spedIcms: projectData.spedIcms || projectData.speds || defaults,
      spedPisCofins: projectData.spedPisCofins || defaults,
      xmls: projectData.xmls || defaults,
      nfes: projectData.nfes || defaults,
      ctes: projectData.ctes || defaults,
      certificados: projectData.certificados || defaults,
      xmlsFaltantes: projectData.xmlsFaltantes || 0,
      periodoInicial: projectData.periodoInicial || '-',
      periodoFinal: projectData.periodoFinal || '-',
      empresas: projectData.empresas || []
    };
  }, [projectData]);

  // Para usuários sem acesso ao dashboard (clientes, etc), mostrar apenas a tela de upload
  if (!hasDashboardAccess) {
    return (
      <Upload 
        onVoltar={() => {
          // Após concluir, recarrega a mesma tela de upload
          fetchUploadedFiles();
        }}
        hideBackButton={true}
      />
    );
  }

  // Se estiver mostrando a tela de upload (para god), renderiza ela
  if (showUploadScreen) {
    return (
      <Upload 
        onVoltar={() => {
          setShowUploadScreen(false);
          fetchProjectData(); // Atualiza dados ao voltar
        }} 
      />
    );
  }

  // Se nenhum arquivo foi enviado, mostrar apenas a dropbox de upload
  const hasNoFiles = !loading && projectData && 
    (dashboardData?.empresasConectadas?.total || 0) === 0 && 
    (dashboardData?.speds?.total || 0) === 0 && 
    (dashboardData?.xmls?.total || 0) === 0 &&
    (dashboardData?.empresas?.length || 0) === 0;

  if (hasNoFiles) {
    return (
      <Upload 
        onVoltar={() => {
          fetchProjectData();
        }}
        hideBackButton={true}
      />
    );
  }

  const displayData = dashboardData || {
    empresasConectadas: { total: 0, processados: 0 },
    speds: { total: 0, processados: 0 },
    spedIcms: { total: 0, processados: 0 },
    spedPisCofins: { total: 0, processados: 0 },
    xmls: { total: 0, processados: 0 },
    certificados: { total: 0, processados: 0 },
    nfes: { total: 0, processados: 0 },
    ctes: { total: 0, processados: 0 },
    xmlsFaltantes: 0,
    periodoInicial: '-',
    periodoFinal: '-',
    empresas: []
  };
  const empresasList = displayData.empresas || [];

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-icon">
          <GoUpload />
        </div>
        <div className="header-details">
          <h3>Upload de Documentos</h3>
          <div className="header-meta">
            <span className="status-tag active">Sistema Online</span>
            <span className="update-tag">Atualizado agora</span>
          </div>
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="action-buttons">
        <div style={{ display: 'flex', gap: '8px' }}>
            <button className="action-btn primary" onClick={() => setShowUploadScreen(true)}>
              <GoUpload />
              Carregar Arquivos
            </button>
            <button className="action-btn" onClick={() => setShowDownloadModal(true)} style={{ color: '#aaa', borderColor: 'rgba(255,255,255,0.1)' }}>
              <GoDownload />
              Downloads
            </button>
        </div>

        {isGod && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="action-btn" 
              onClick={handleReprocessSpeds}
              disabled={reprocessing}
              style={{ 
                color: reprocessing ? '#666' : '#3b82f6', 
                borderColor: reprocessing ? 'rgba(255,255,255,0.1)' : 'rgba(59, 130, 246, 0.3)',
                cursor: reprocessing ? 'not-allowed' : 'pointer',
                opacity: reprocessing ? 0.6 : 1
              }}
            >
              <GoSync className={reprocessing ? 'spin' : ''} />
              {reprocessing ? 'Reprocessando...' : 'Reprocessar'}
            </button>
            <button className="action-btn danger" onClick={() => setShowResetModal(true)}>
              <GoTrash />
              Resetar Banco
            </button>
          </div>
        )}
      </div>

      {/* KPI Cards - Linha 1 */}
      {loading || !projectData ? (
        <div className="kpi-cards main-stats">
          <KpiSkeleton theme={theme} />
          <KpiSkeleton theme={theme} />
          <KpiSkeleton theme={theme} />
          <KpiSkeleton theme={theme} />
          <KpiSkeleton theme={theme} />
        </div>
      ) : (
      <div className="kpi-cards main-stats">
        <div className={`kpi-card primary${displayData.empresasConectadas.total > 0 ? ' is-complete' : ''}`}>
          <div className="card-content">
            <div className="thumb">
              <GoOrganization />
            </div>
            <div className="text">
              <p>{displayData.empresasConectadas.total}</p>
              <p>Empresas Conectadas</p>
            </div>
          </div>
        </div>

        <div className={`kpi-card success with-progress${displayData.spedIcms.total > 0 && displayData.spedIcms.processados === displayData.spedIcms.total ? ' is-complete' : ''}`}>
          <div className="card-content">
            <div className="thumb">
              <GoFile />
            </div>
            <div className="text">
              <p>
                {displayData.spedIcms.processados}/{displayData.spedIcms.total}
              </p>
              <p>SPED EFD ICMS IPI</p>
            </div>
          </div>
          <div className="kpi-progress">
            <div
              className="kpi-progress-fill"
              style={{
                width: `${
                  (displayData.spedIcms.processados /
                    (displayData.spedIcms.total || 1)) *
                  100
                }%`,
              }}
            />
          </div>
        </div>

        <div className={`kpi-card sped-pis with-progress${displayData.spedPisCofins.total > 0 && displayData.spedPisCofins.processados === displayData.spedPisCofins.total ? ' is-complete' : ''}`}>
          <div className="card-content">
            <div className="thumb">
              <GoFile />
            </div>
            <div className="text">
              <p>
                {displayData.spedPisCofins.processados}/
                {displayData.spedPisCofins.total}
              </p>
              <p>SPED EFD PIS COFINS</p>
            </div>
          </div>
          <div className="kpi-progress">
            <div
              className="kpi-progress-fill"
              style={{
                width: `${
                  (displayData.spedPisCofins.processados /
                    (displayData.spedPisCofins.total || 1)) *
                  100
                }%`,
              }}
            />
          </div>
        </div>

        <div className={`kpi-card info with-progress${displayData.xmls.total > 0 && displayData.xmls.processados === displayData.xmls.total ? ' is-complete' : ''}`}>
          <div className="card-content">
            <div className="thumb">
              <HiOutlineDocumentText />
            </div>
            <div className="text">
              <p>
                {formatNumber(displayData.xmls.processados)}/
                {formatNumber(displayData.xmls.total)}
              </p>
              <p>XMLs</p>
            </div>
          </div>
          <div className="kpi-progress">
            <div
              className="kpi-progress-fill"
              style={{
                width: `${
                  (displayData.xmls.processados / (displayData.xmls.total || 1)) * 100
                }%`,
              }}
            />
          </div>
        </div>

        <div className={`kpi-card warning with-progress${displayData.certificados.total > 0 && displayData.certificados.processados === displayData.certificados.total ? ' is-complete' : ''}`}>
          <div className="card-content">
            <div className="thumb">
              <GoKey />
            </div>
            <div className="text">
              <p>
                {displayData.certificados.processados}/
                {displayData.certificados.total}
              </p>
              <p>Certificados Digitais</p>
            </div>
          </div>
          <div className="kpi-progress">
            <div
              className="kpi-progress-fill"
              style={{
                width: `${
                  (displayData.certificados.processados /
                    (displayData.certificados.total || 1)) *
                  100
                }%`,
              }}
            />
          </div>
        </div>
      </div>
      )}

      {/* KPI Cards - Linha 2 */}
      {/* KPI Cards - Linha 2 */}
      {loading || !projectData ? (
        <div className="kpi-cards secondary-stats">
           <KpiSkeleton theme={theme} />
           <KpiSkeleton theme={theme} />
           <KpiSkeleton theme={theme} />
           <KpiSkeleton theme={theme} />
           <KpiSkeleton theme={theme} />
        </div>
      ) : (
      <div className="kpi-cards secondary-stats">
        <div className="kpi-card compact">
          <div className="card-content">
            <div className="thumb">
              <GoCalendar />
            </div>
            <div className="text">
              <p>{displayData.periodoInicial}</p>
              <p>Período Inicial</p>
            </div>
          </div>
        </div>

        <div className="kpi-card compact">
          <div className="card-content">
            <div className="thumb">
              <GoCalendar />
            </div>
            <div className="text">
              <p>{displayData.periodoFinal}</p>
              <p>Período Final</p>
            </div>
          </div>
        </div>

        <div className="kpi-card compact nfe with-progress">
          <div className="card-content">
            <div className="thumb">
              <GoAlert />
            </div>
            <div className="text">
              <p>
                {formatNumber(displayData.nfes.processados)}/
                {formatNumber(displayData.nfes.total)}
              </p>
              <p>NFes</p>
            </div>
          </div>
          <div className="kpi-progress">
            <div
              className="kpi-progress-fill"
              style={{
                width: `${
                  (displayData.nfes.processados / (displayData.nfes.total || 1)) * 100
                }%`,
              }}
            />
          </div>
        </div>

        <div className="kpi-card compact cte with-progress">
          <div className="card-content">
            <div className="thumb">
              <HiOutlineDocumentText />
            </div>
            <div className="text">
              <p>
                {formatNumber(displayData.ctes.processados)}/
                {formatNumber(displayData.ctes.total)}
              </p>
              <p>CTes</p>
            </div>
          </div>
          <div className="kpi-progress">
            <div
              className="kpi-progress-fill"
              style={{
                width: `${
                  (displayData.ctes.processados / (displayData.ctes.total || 1)) * 100
                }%`,
              }}
            />
          </div>
        </div>

        <div className="kpi-card compact rejected">
          <div className="card-content">
            <div className="thumb">
              <GoAlert />
            </div>
            <div className="text">
              <p>{formatNumber(displayData.xmlsFaltantes)}</p>
              <p>XML's Faltantes</p>
            </div>
          </div>
        </div>
      </div>
      )}


      {/* Abas de Navegação */}
      <>
      <div className="files-tabs">
        <button
          className={activeSection === 'informacoes' ? 'active' : ''}
          onClick={() => setActiveSection('informacoes')}
        >
          <GoOrganization />
          Informações
        </button>
        <button
          className={activeSection === 'arquivos' ? 'active' : ''}
          onClick={() => setActiveSection('arquivos')}
        >
          <GoFile />
          Arquivos Enviados
        </button>
      </div>

      {/* Seção: Informações (Empresas) */}
      {activeSection === 'informacoes' && (
        <div className="empresas-grid">
          {loading || !projectData ? (
             <>
               <CompanyCardSkeleton theme={theme} />
               <CompanyCardSkeleton theme={theme} />
               <CompanyCardSkeleton theme={theme} />
               <CompanyCardSkeleton theme={theme} />
               <CompanyCardSkeleton theme={theme} />
               <CompanyCardSkeleton theme={theme} />
             </>
          ) : (
            <>
              {empresasList.length === 0 && <p>Nenhuma empresa encontrada.</p>}
        {empresasList.map((empresa) => {
          // Defaults seguros (card compacto usa apenas xmls/nfes/ctes; resto vai no modal)
          const xmls = empresa.xmls || { processados: 0, total: 0 };
          const nfes = empresa.nfes || { processados: 0, total: 0 };
          const ctes = empresa.ctes || { processados: 0, total: 0 };

          const xmlsPercent = (xmls.processados / (xmls.total || 1)) * 100;

          // Heatmap mini: ano corrente (último com dados)
          const actData = empresa.activityData || {};
          const yearsInData = Array.from(new Set(Object.keys(actData).map(k => k.split('-')[0]))).filter(Boolean).sort();
          const currentYear = yearsInData[yearsInData.length - 1] || String(new Date().getFullYear());
          const miniMonths = Array.from({ length: 12 }).map((_, i) => {
            const m = String(i + 1).padStart(2, '0');
            const key = `${currentYear}-${m}`;
            const data = actData[key];
            const total = (data && typeof data === 'object') ? (data.total || 0) : (data || 0);
            return total > 0;
          });

          return (
            <div
              key={empresa.id || Math.random()}
              className={`empresa-card-new ${
                selectedEmpresa?.id === empresa.id ? "selected" : ""
              }`}
              onClick={() => setEmpresaModal(empresa)}
            >
              {/* Header */}
              <div className="card-header">
                <div className="empresa-info">
                  <span className="empresa-nome">{empresa.nome}</span>
                  <span className="empresa-cnpj">{empresa.cnpj}</span>
                </div>
                <div
                  className="empresa-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadMissingKeys(empresa.id);
                  }}
                  title="Baixar Chaves Faltantes"
                >
                  <span className="icon-wrapper default"><GoPackage /></span>
                  <span className="icon-wrapper hover"><GoDownload /></span>
                </div>
              </div>

              {/* Período em 1 linha */}
              <div className="periodo-line">
                <span>{empresa.periodoInicio || '—'}</span>
                <span className="periodo-arrow">→</span>
                <span>{empresa.periodoFim || '—'}</span>
              </div>

              {/* 3 micro-stats */}
              <div className="micro-stats">
                <div className="micro-stat">
                  <div className="micro-stat-row">
                    <span className="micro-stat-label">XMLs</span>
                    <span className={`micro-stat-value${xmls.total > 0 && xmls.processados === xmls.total ? ' complete' : ''}`}>
                      {formatNumber(xmls.processados)}/{formatNumber(xmls.total)}
                    </span>
                  </div>
                  <div className="micro-progress">
                    <div className="micro-progress-fill" style={{ width: `${xmlsPercent}%` }} />
                  </div>
                </div>

                <div className="micro-stat">
                  <div className="micro-stat-row">
                    <span className="micro-stat-label">NFe</span>
                    <span className={`micro-stat-value${nfes.total > 0 && nfes.processados === nfes.total ? ' complete' : ''}`}>
                      {formatNumber(nfes.processados)}/{formatNumber(nfes.total)}
                    </span>
                  </div>
                </div>

                <div className="micro-stat">
                  <div className="micro-stat-row">
                    <span className="micro-stat-label">CTe</span>
                    <span className={`micro-stat-value${ctes.total > 0 && ctes.processados === ctes.total ? ' complete' : ''}`}>
                      {formatNumber(ctes.processados)}/{formatNumber(ctes.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Heatmap mini */}
              <div className="heatmap-mini">
                <div className="heatmap-mini-header">
                  <span className="heatmap-mini-label">Atividade</span>
                  <span className="heatmap-mini-year">{currentYear}</span>
                </div>
                <div className="heatmap-mini-grid">
                  {miniMonths.map((hasData, i) => (
                    <div key={i} className={`heatmap-mini-cell${hasData ? ' has-data' : ''}`} />
                  ))}
                </div>
              </div>

              {/* Cert pill */}
              <div className={`cert-pill ${empresa.certificado ? 'presente' : 'ausente'}`}>
                {empresa.certificado ? <GoCheck /> : <GoAlert />}
                <span>
                  {empresa.certificado
                    ? `Válido ${new Date(empresa.certificado.validTo).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                    : 'Sem certificado'}
                </span>
              </div>
            </div>
          );
        })}
        </>
      )}
        </div>
      )}

      {/* Seção: Arquivos Enviados */}
      {activeSection === 'arquivos' && (
        <div className="arquivos-section" style={{ padding: '20px 0' }}>
          {loadingFiles && <p style={{ color: '#888' }}>Carregando arquivos...</p>}
          
          {!loadingFiles && uploadedFiles.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon"><GoArchive /></div>
              <p className="empty-title">Nenhum arquivo enviado ainda.</p>
              <p className="empty-hint">Clique em "Carregar Arquivos" para enviar .zip, .rar ou .txt</p>
            </div>
          )}



          {!loadingFiles && uploadedFiles.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {uploadedFiles.map((file, idx) => {
                const projectId = localStorage.getItem('vidal_selected_project');


                const handleDelete = async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Excluir "${file.originalName || file.filename}"?`)) return;
                  
                  try {
                    await api.delete(`/projects/${projectId}/uploads/${file.id}`);
                    fetchUploadedFiles();
                  } catch (error) {
                    alert('Erro ao excluir arquivo');
                  }
                };

                const handleDownload = (e) => {
                    e.stopPropagation();
                    const API_URL = process.env.REACT_APP_API || 'https://api.vidal-app.com';
                    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
                    const token = authData.token;
                    
                    if (!token) {
                      alert("Erro de autenticação. Tente fazer login novamente.");
                      return;
                    }

                    const downloadUrl = `${API_URL}/projects/${projectId}/uploads/download/${file.filename}?token=${token}`;
                    window.open(downloadUrl, '_blank');
                };

                const statusClass = file.status === 'processed' ? 'processed' : 
                                   file.status === 'error' ? 'error' : 'uploaded';
                
                return (
                  <div key={file.id || idx} className="arquivo-card">
                    <div className="arquivo-icon">
                      <GoArchive />
                    </div>
                    
                    <div className="arquivo-info">
                      <div className="arquivo-nome">
                        {file.originalName || file.filename}
                      </div>
                      <div className="arquivo-meta">
                        <span>{file.extension?.toUpperCase().replace('.', '')}</span>
                        <span className="separator">•</span>
                        <span>{file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : '-'}</span>
                        <span className="separator">•</span>
                        <span>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleString('pt-BR') : '-'}</span>
                      </div>
                    </div>

                    <div className="arquivo-actions">
                      <span className={`status-badge ${statusClass}`}>
                        {file.status === 'uploaded' ? 'Enviado' :
                         file.status === 'processing' ? 'Processando' :
                         file.status === 'processed' ? 'Processado' :
                         file.status === 'error' ? 'Erro' : file.status}
                      </span>

                      <button
                        onClick={handleDownload}
                        className="action-btn download"
                        title="Baixar arquivo"
                      >
                        <GoUpload style={{ transform: 'rotate(180deg)' }} />
                      </button>

                      <button
                        onClick={handleDelete}
                        className="action-btn delete"
                        title="Excluir arquivo"
                      >
                        <GoTrash />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </>

      {/* Modal de Confirmação de Reset */}
      {showResetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            border: '1px solid #333'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <GoTrash style={{ fontSize: '28px', color: '#f43f5e' }} />
              <h2 style={{ margin: 0, color: '#fff', fontSize: '20px' }}>Resetar Dados SPED</h2>
            </div>
            
            {!resetting ? (
              <>
                <p style={{ color: '#aaa', marginBottom: '24px', lineHeight: '1.6' }}>
                  Esta ação irá <strong style={{ color: '#f43f5e' }}>excluir permanentemente</strong> todos os dados dos índices SPED (ICMS/IPI e PIS/COFINS) relacionados a este projeto.
                  <br /><br />
                  Os índices afetados incluem: 0000, C100, C170, C190, D100, entre outros.
                  <br /><br />
                  <strong>Esta ação não pode ser desfeita.</strong>
                </p>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => setShowResetModal(false)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      background: 'transparent',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleResetSpedData}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#f43f5e',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Confirmar Exclusão
                  </button>
                </div>
              </>
            ) : (
              <div style={{ padding: '20px 0' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '12px' 
                }}>
                  <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>
                    Excluindo dados...
                  </span>
                  <span style={{ color: '#10b981', fontSize: '18px', fontWeight: 600 }}>
                    {resetProgress}%
                  </span>
                </div>
                
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: `${resetProgress}%`,
                    height: '100%',
                    background: resetProgress >= 100 ? '#10b981' : '#f43f5e',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease-out'
                  }} />
                </div>
                
                <p style={{ 
                  color: resetProgress >= 100 ? '#10b981' : '#888', 
                  fontSize: '14px',
                  margin: 0,
                  textAlign: 'center'
                }}>
                  {resetStage || 'Processando...'}
                </p>
                
                {resetProgress > 0 && resetProgress < 100 && (
                  <p style={{ 
                    color: '#666', 
                    fontSize: '12px',
                    margin: '12px 0 0 0',
                    textAlign: 'center'
                  }}>
                    Não feche esta janela até a conclusão.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Modal de Downloads */}
      {showDownloadModal && (
        <ModalOverlay onClick={() => setShowDownloadModal(false)}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h3>
                <GoDownload size={24} color="#10b981" /> 
                Downloads Disponíveis
              </h3>
              <GoX size={24} style={{ cursor: 'pointer' }} onClick={() => setShowDownloadModal(false)} />
            </ModalHeader>
            
            <ModalBody>
               <p style={{ color: '#888', fontSize: '14px', marginBottom: '8px' }}>
                  Selecione o tipo de arquivo ou relatório que deseja gerar:
               </p>
               
               {/* 1. Lista de chaves faltantes (Azul) */}
               <DownloadCard onClick={() => handleDownloadMissingKeys()} style={{ cursor: 'pointer' }} accentColor="#3b82f6">
                  <DownloadIconWrapper bgColor="#3b82f615" iconColor="#3b82f6">
                     <GoKey />
                  </DownloadIconWrapper>
                  <div style={{ flex: 1 }}>
                     <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px', color: ({theme}) => theme.text || '#fff' }}>
                        Lista de chaves faltantes
                     </div>
                     <div style={{ fontSize: '13px', opacity: 0.7, color: ({theme}) => theme.text || '#aaa' }}>
                        Exportar lista de chaves de XMLs faltantes em relação ao SPED.
                     </div>
                  </div>
                  <GoChevronRight size={20} style={{ opacity: 0.5 }} />
               </DownloadCard>

               {/* 2. SPED's enviados (Verde) */}
               <DownloadCard 
                 onClick={!isDownloadingSped ? handleDownloadSpedFiles : undefined} 
                 style={{ cursor: isDownloadingSped ? 'wait' : 'pointer', position: 'relative', overflow: 'hidden' }} 
                 accentColor="#10b981"
               >
                  <DownloadIconWrapper bgColor="#10b98115" iconColor="#10b981">
                     {isDownloadingSped ? (
                       <div style={{ 
                         width: 20, height: 20, 
                         border: '2px solid #10b981', 
                         borderTopColor: 'transparent', 
                         borderRadius: '50%', 
                         animation: 'spin 1s linear infinite' 
                       }} />
                     ) : (
                       <GoFile />
                     )}
                  </DownloadIconWrapper>
                  <div style={{ flex: 1 }}>
                     <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px', color: ({theme}) => theme.text || '#fff' }}>
                        {isDownloadingSped ? `Baixando... ${spedDownloadProgress}%` : 'SPEDs Enviados'}
                     </div>
                     <div style={{ fontSize: '13px', opacity: 0.7, color: ({theme}) => theme.text || '#aaa' }}>
                        {isDownloadingSped ? 'Compactando arquivos...' : 'Download dos arquivos TXT originais enviados.'}
                     </div>
                  </div>
                  {!isDownloadingSped && <GoChevronRight size={20} style={{ opacity: 0.5 }} />}
                  
                  {/* Progress Bar */}
                  {isDownloadingSped && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: `${spedDownloadProgress}%`,
                      height: '3px',
                      backgroundColor: '#10b981',
                      transition: 'width 0.3s ease-out'
                    }} />
                  )}
               </DownloadCard>

               {/* 3. Relatório de arquivos enviados (Laranja) */}
               <DownloadCard onClick={handleDownloadFilesReport} style={{ cursor: 'pointer' }} accentColor="#f59e0b">
                  <DownloadIconWrapper bgColor="#f59e0b15" iconColor="#f59e0b">
                     <BiSpreadsheet />
                  </DownloadIconWrapper>
                  <div style={{ flex: 1 }}>
                     <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px', color: ({theme}) => theme.text || '#fff' }}>
                        Relatório de arquivos enviados
                     </div>
                     <div style={{ fontSize: '13px', opacity: 0.7, color: ({theme}) => theme.text || '#aaa' }}>
                        Relatório detalhado dos arquivos processados no sistema.
                     </div>
                  </div>
                  <GoChevronRight size={20} style={{ opacity: 0.5 }} />
               </DownloadCard>

            </ModalBody>
            
            <ModalFooter>
              <ActionButton onClick={() => setShowDownloadModal(false)}>Fechar</ActionButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Modal de Detalhes da Empresa — compacto e sofisticado */}
      {empresaModal && (() => {
        const empresa = empresaModal;
        const xmls = empresa.xmls || { processados: 0, total: 0 };
        const nfes = empresa.nfes || { processados: 0, total: 0 };
        const ctes = empresa.ctes || { processados: 0, total: 0 };
        const spedIcms = empresa.spedIcms || { processados: 0, total: 0 };
        const spedPisCofins = empresa.spedPisCofins || { processados: 0, total: 0 };
        const entradas = {
          total: nfes.entradas?.total || 0,
          processados: nfes.entradas?.processados || 0,
        };
        const saidas = {
          total: nfes.saidas?.total || 0,
          processados: nfes.saidas?.processados || 0,
        };
        const pct = (a, b) => Math.round((a / (b || 1)) * 100);
        const xmlsP = pct(xmls.processados, xmls.total);
        const nfesP = pct(nfes.processados, nfes.total);
        const ctesP = pct(ctes.processados, ctes.total);
        const faltantes = Math.max(0, xmls.total - xmls.processados);
        const isComplete = (a, b) => b > 0 && a === b;

        const actData = empresa.activityData || {};
        const yearsSet = new Set();
        Object.keys(actData).forEach(k => {
          const y = k.split('-')[0];
          if (y && /^\d{4}$/.test(y)) yearsSet.add(y);
        });
        const years = Array.from(yearsSet).sort();
        const monthsAbbr = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'];

        let maxMissing = 0;
        years.forEach(y => {
          for (let i = 1; i <= 12; i++) {
            const m = String(i).padStart(2, '0');
            const d = actData[`${y}-${m}`];
            if (d && typeof d === 'object') {
              const nf = d.nfes || { total: 0, processed: 0 };
              const ct = d.ctes || { total: 0, processed: 0 };
              const missing = (nf.total + ct.total) - (nf.processed + ct.processed);
              if (missing > maxMissing) maxMissing = missing;
            }
          }
        });

        return (
          <ModalOverlay onClick={() => setEmpresaModal(null)}>
            <EmpresaModalContainer onClick={(e) => e.stopPropagation()}>
              {/* HEADER */}
              <div className="empresa-modal-header">
                <div className="empresa-modal-title">
                  <span className="empresa-eyebrow">EMPRESA</span>
                  <h2>{empresa.nome}</h2>
                  <span className="empresa-modal-cnpj">{empresa.cnpj}</span>
                </div>
                <div className="empresa-modal-close" onClick={() => setEmpresaModal(null)} title="Fechar">
                  <GoX />
                </div>
              </div>

              {/* BODY */}
              <div className="empresa-modal-body">
                {/* Período inline */}
                <div className="periodo-inline">
                  <span className="periodo-tag">PERÍODO</span>
                  <span className="periodo-value">{empresa.periodoInicio || '—'}</span>
                  <span className="periodo-arrow">→</span>
                  <span className="periodo-value">{empresa.periodoFim || '—'}</span>
                </div>

                {/* Stats principais */}
                <div className="section">
                  <div className="section-eyebrow">Documentos Eletrônicos</div>
                  <div className="stats-section">
                    {[
                      { label: 'XMLs', proc: xmls.processados, total: xmls.total, percent: xmlsP },
                      { label: 'NFe',  proc: nfes.processados, total: nfes.total, percent: nfesP },
                      { label: 'CTe',  proc: ctes.processados, total: ctes.total, percent: ctesP },
                    ].map((s) => (
                      <div className="stat-row" key={s.label}>
                        <span className="stat-label">{s.label}</span>
                        <div className="stat-progress">
                          <div className="stat-progress-fill" style={{ width: `${s.percent}%` }} />
                        </div>
                        <span className={`stat-percent${isComplete(s.proc, s.total) ? ' complete' : ''}`}>
                          {s.percent}%
                        </span>
                        <span className={`stat-value${isComplete(s.proc, s.total) ? ' complete' : ''}`}>
                          {formatNumber(s.proc)}/{formatNumber(s.total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pairs — tabela */}
                <div className="section">
                  <div className="section-eyebrow">Movimentação & SPED</div>
                  <div className="pairs-list">
                    <div className="pair-row">
                      <span className="pair-label">Entradas</span>
                      <span className={`pair-value${isComplete(entradas.processados, entradas.total) ? ' complete' : ''}`}>
                        {formatNumber(entradas.processados)}/{formatNumber(entradas.total)}
                      </span>
                    </div>
                    <div className="pair-row">
                      <span className="pair-label">Saídas</span>
                      <span className={`pair-value${isComplete(saidas.processados, saidas.total) ? ' complete' : ''}`}>
                        {formatNumber(saidas.processados)}/{formatNumber(saidas.total)}
                      </span>
                    </div>
                    <div className="pair-row">
                      <span className="pair-label">ICMS / IPI</span>
                      <span className={`pair-value${isComplete(spedIcms.processados, spedIcms.total) ? ' complete' : ''}`}>
                        {spedIcms.processados}/{spedIcms.total}
                      </span>
                    </div>
                    <div className="pair-row">
                      <span className="pair-label">PIS / COFINS</span>
                      <span className={`pair-value${isComplete(spedPisCofins.processados, spedPisCofins.total) ? ' complete' : ''}`}>
                        {spedPisCofins.processados}/{spedPisCofins.total}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Faltantes inline */}
                {faltantes > 0 && (
                  <div className="faltantes-inline">
                    <span className="faltantes-label">
                      <GoAlert />
                      XMLs Faltantes
                    </span>
                    <span className="faltantes-value">{formatNumber(faltantes)}</span>
                  </div>
                )}

                {/* Certificado — linha discreta */}
                <div className={`cert-inline ${empresa.certificado ? 'presente' : 'ausente'}`}>
                  <span className="cert-dot" />
                  <span>
                    {empresa.certificado
                      ? `Certificado válido até ${new Date(empresa.certificado.validTo).toLocaleDateString('pt-BR')}`
                      : 'Sem certificado digital'}
                  </span>
                </div>

                {/* Histórico mensal */}
                {years.length > 0 && (
                  <div className="section">
                    <div className="section-eyebrow">Histórico Mensal</div>
                    <div className="historico-grid">
                      {/* Months labels */}
                      <div className="historico-months-label">
                        <span />
                        {monthsAbbr.map((m, i) => <span key={i}>{m[0]}</span>)}
                      </div>
                      {years.map(year => (
                        <div key={year} className="historico-row">
                          <span className="historico-year">{year}</span>
                          {Array.from({ length: 12 }).map((_, i) => {
                            const m = String(i + 1).padStart(2, '0');
                            const key = `${year}-${m}`;
                            const data = actData[key] || null;
                            const isObj = data && typeof data === 'object';
                            const count = isObj ? (data.total || 0) : (data || 0);

                            const nfeD = isObj ? (data.nfes || { total: 0, processed: 0 }) : { total: 0, processed: 0 };
                            const cteD = isObj ? (data.ctes || { total: 0, processed: 0 }) : { total: 0, processed: 0 };
                            const icmsD = isObj ? (data.icms || { total: 0, processed: 0 }) : { total: 0, processed: 0 };
                            const pisD  = isObj ? (data.pis  || { total: 0, processed: 0 }) : { total: 0, processed: 0 };
                            const totalXmls = nfeD.total + cteD.total;
                            const procXmls = nfeD.processed + cteD.processed;

                            let opacity = 0;
                            if (count > 0) {
                              if (totalXmls > 0) {
                                const missing = totalXmls - procXmls;
                                if (missing === 0) opacity = 1;
                                else {
                                  const ratio = maxMissing > 0 ? (missing / maxMissing) : 0;
                                  opacity = Math.max(0.3, 1 - ratio * 0.7);
                                }
                              } else {
                                opacity = Math.min(0.3 + Math.log(count + 1) / 5, 1);
                              }
                            }

                            return (
                              <div key={key} className={`historico-cell${count > 0 ? ' has-data' : ''}`}>
                                <div className="cell-content" style={count > 0 ? { opacity } : { opacity: 0 }} />
                                {count > 0 && (
                                  <div className="historico-tooltip">
                                    <div className="tt-title">{monthsAbbr[i]} · {year}</div>
                                    {isObj ? (
                                      <>
                                        <div className="tt-row">
                                          <span>XMLs</span>
                                          <span>{formatNumber(procXmls)}/{formatNumber(totalXmls)}</span>
                                        </div>
                                        <div className="tt-row">
                                          <span>NFe</span>
                                          <span>{formatNumber(nfeD.processed)}/{formatNumber(nfeD.total)}</span>
                                        </div>
                                        <div className="tt-row">
                                          <span>CTe</span>
                                          <span>{formatNumber(cteD.processed)}/{formatNumber(cteD.total)}</span>
                                        </div>
                                        <div className="tt-row">
                                          <span>ICMS/IPI</span>
                                          <span>{icmsD.processed}/{icmsD.total}</span>
                                        </div>
                                        <div className="tt-row">
                                          <span>PIS/COFINS</span>
                                          <span>{pisD.processed}/{pisD.total}</span>
                                        </div>
                                        {(totalXmls - procXmls) > 0 && (
                                          <div className="tt-row warning">
                                            <span>Faltantes</span>
                                            <span>{formatNumber(totalXmls - procXmls)}</span>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="tt-row">
                                        <span>Total</span>
                                        <span>{formatNumber(count)}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* FOOTER */}
              <div className="empresa-modal-footer">
                <ActionButton onClick={() => setEmpresaModal(null)}>Fechar</ActionButton>
                <ActionButton primary onClick={() => handleDownloadMissingKeys(empresa.id)}>
                  <GoDownload />
                  Baixar Chaves Faltantes
                </ActionButton>
              </div>
            </EmpresaModalContainer>
          </ModalOverlay>
        );
      })()}

    </Container>
  );
}
